import React, { useState } from "react";
import {
  RadioCardIndicator,
  RadioCardItem,
} from "components/Graphs/RadioCardGroup";
import { RadioCardGroup } from "components/Graphs/RadioCardGroup";
import { Box, LayoutGrid, Layers, FileCode } from "lucide-react";
import { FlatCard } from "components/UI-lib";
import { Label } from "recharts";
import {
  ChevronDownIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  ServerStackIcon,
  Square3Stack3DIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { Tooltip } from "@mui/material";
import { FadeInDiv } from "components/Motion/FadeInDiv";
import {
  CustomContainerSelector,
  FeaturedContainer,
} from "components/Environment/Create/DrawerContainerSelector";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import {
  ContainerConfiguration,
  UIBuilderContainer,
} from "./container-configuration";

export type ContainerMode = "featured" | "compose" | null;

interface ContainerModeSelectorProps {
  selectedMode: ContainerMode;
  onModeSelect: (mode: ContainerMode) => void;
  useExistingCompose: boolean;
  onExistingComposeToggle: (value: boolean) => void;
  selectedContainer: ContainerSelected | undefined;
  setSelectedContainer: (container: ContainerSelected) => void;
  pythonVersion: string;
  setPythonVersion: (version: string) => void;
  cudaVersion: string;
  setCudaVersion: (version: string) => void;
  UIBuilderContainers: UIBuilderContainer[];
  setUIBuilderContainers: (containers: UIBuilderContainer[]) => void;
  onResetDockerCompose: () => void;
}

export function ContainerModeSelector({
  selectedMode,
  onModeSelect,
  useExistingCompose,
  onExistingComposeToggle,
  selectedContainer,
  setSelectedContainer,
  pythonVersion,
  setPythonVersion,
  cudaVersion,
  setCudaVersion,
  UIBuilderContainers,
  setUIBuilderContainers,
  onResetDockerCompose,
}: ContainerModeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <div className="flex flex-row justify-between mb-6">
        <div className="flex flex-col">
          <h2 className="text-2xl font-bold text-primary">
            Choose a Container Configuration
          </h2>
          <div className="mt-2">
            <div className="flex flex-col">
              <motion.div
                className="p-2 bg-secondaryBg rounded-md text-sm text-primary cursor-pointer flex flex-col"
                onClick={() => setIsOpen(!isOpen)}
                initial={false}
              >
                <div className="flex items-center">
                  <p className="font-medium text-secondary mr-2">
                    Which option would best suit my launchable?
                  </p>
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDownIcon className="w-5 h-5 text-secondary" />
                  </motion.div>
                </div>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.ul
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="list-disc pl-5 text-secondary mt-2"
                    >
                      <div className="flex flex-col space-y-1">
                        <li>
                          <strong>Featured Container:</strong> Best for simple
                          demos with a quick setup with a jupyter notebook
                          experience.
                        </li>
                        <li>
                          <strong>Docker Compose:</strong> Best for showcasing
                          complex workflows with multiple containers.
                        </li>
                      </div>
                      <p className="text-xs text-secondary mt-2">
                        Note: If your container requires a{" "}
                        <strong>private registry</strong> or{" "}
                        <strong>api keys</strong> to pull the image,{" "}
                        <strong>click the back button below</strong> and{" "}
                        <strong>select the VM Mode</strong> option. Then, in
                        your uploaded notebook file, you should specify the
                        steps to authenticate and pull the image.
                      </p>
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <RadioCardGroup
        value={selectedMode || ""}
        onValueChange={(value) => onModeSelect(value as ContainerMode)}
        className="grid grid-cols-1 gap-6 md:grid-cols-2"
      >
        <RadioCardItem
          value="featured"
          id="featured"
          className={`relative cursor-pointer bg-primaryBg border flex flex-col items-start ${
            selectedMode === "featured" ? "border-highlight" : "border-zinc-800"
          }`}
        >
          <div className="pb-2">
            <div className="flex items-center justify-between">
              <label htmlFor="featured" className="flex-1 cursor-pointer">
                <div className="text-lg flex items-center text-primary">
                  <CubeIcon className="mr-2 h-5 w-5 text-highlight" />
                  Featured Container
                </div>
              </label>
            </div>
            <p className="text-secondary text-sm">
              Choose from a pre-configured container with Jupyter support
            </p>
          </div>
          <div>
            <ul className="list-disc pl-5 text-sm space-y-2 text-secondary">
              <li>Ready-to-use containers</li>
              <li>Jupyter pre-configured on port 8888</li>
            </ul>
          </div>
        </RadioCardItem>

        <RadioCardItem
          value="compose"
          id="compose"
          className={`relative cursor-pointer bg-primaryBg border flex flex-col items-start ${
            selectedMode === "compose" ? "border-highlight" : "border-zinc-800"
          }`}
        >
          <div className="pb-2">
            <div className="flex items-center justify-between">
              <label htmlFor="compose" className="flex-1 cursor-pointer">
                <div className="text-lg flex items-center text-primary">
                  <FileCode className="mr-2 h-5 w-5 text-highlight" />
                  Docker Compose
                </div>
              </label>
            </div>
            <p className="text-secondary text-sm">
              Single or Multi-container setup with Docker Compose
            </p>
          </div>
          <div>
            <ul className="list-disc pl-5 text-sm space-y-2 text-secondary">
              <li>Single or Multiple containers</li>
              <li>Upload, create, or reference a docker-compose file</li>
            </ul>
          </div>
        </RadioCardItem>
      </RadioCardGroup>
      <div className="mt-4">
        {selectedMode === "featured" && (
          <FadeInDiv>
            <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
            <FeaturedContainer
              selectedContainer={selectedContainer}
              setSelectedContainer={setSelectedContainer}
              pythonVersion={pythonVersion}
              setPythonVersion={setPythonVersion}
              cudaVersion={cudaVersion}
              setCudaVersion={setCudaVersion}
            />
          </FadeInDiv>
        )}

        {selectedMode === "compose" && (
          <FadeInDiv>
            <hr className="w-[100%] mb-3 mt-3 dark:border dark:border-zinc-800" />
            <ContainerConfiguration
              UIBuilderContainers={UIBuilderContainers}
              onUIBuilderContainersChange={(containers) => {
                setUIBuilderContainers(containers);
              }}
              useExistingCompose={useExistingCompose}
              onExistingComposeToggle={onExistingComposeToggle}
              selectedContainer={selectedContainer}
              setSelectedContainer={setSelectedContainer}
              onResetDockerCompose={onResetDockerCompose}
            />
          </FadeInDiv>
        )}
      </div>
    </div>
  );
}

export default ContainerModeSelector;
